import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import {
  formatTimestamp,
  type DocumentSkuOption,
  type ProductCollection,
  type ProductTransitionNotice,
} from "@/lib/documents";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { SkuSearchableSelect } from "@/components/mrp/SkuSearchableSelect";
import { DocumentModal } from "./DocumentModal";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

const QUERY_KEY = ["document-transition-notices"] as const;

const TRANSITION_TYPES: Array<ProductTransitionNotice["transition_type"]> = [
  "immediate_cutover",
  "runout",
  "date_based",
  "order_based",
  "lot_based",
  "hybrid",
];

function statusClass(status: ProductTransitionNotice["status"]): string {
  if (status === "issued") return "bg-blue-50 text-blue-700";
  if (status === "completed") return "bg-green-50 text-green-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

function skuLabel(sku: string | null, productName: string | null): string {
  if (!sku) return "-";
  return productName ? `${sku} - ${productName}` : sku;
}

export function DocumentTransitionsPage() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("documents");
  const documentsReadOnlyTitle = "Your role has read-only access to the Document Control hub module.";
  const [createOpen, setCreateOpen] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromSkuQuery, setFromSkuQuery] = useState("");
  const [toSkuQuery, setToSkuQuery] = useState("");
  const [productCollectionId, setProductCollectionId] = useState("");
  const [noticeNumber, setNoticeNumber] = useState("");
  const [title, setTitle] = useState("");
  const [transitionType, setTransitionType] = useState<ProductTransitionNotice["transition_type"]>("runout");
  const [fromSku, setFromSku] = useState("");
  const [toSku, setToSku] = useState("");
  const [effectiveRule, setEffectiveRule] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [appliesTo, setAppliesTo] = useState("Manufacturing, planning, purchasing, sales order fulfillment");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const collectionsQuery = useQuery<{ collections: ProductCollection[] }, ApiError>({
    queryKey: ["document-collections"],
    queryFn: () => apiFetch<{ collections: ProductCollection[] }>("/api/document-collections"),
  });

  const noticesQuery = useQuery<{ transitionNotices: ProductTransitionNotice[] }, ApiError>({
    queryKey: [...QUERY_KEY, collectionFilter, statusFilter],
    queryFn: () =>
      apiFetch<{ transitionNotices: ProductTransitionNotice[] }>("/api/document-transition-notices", {
        query: {
          productCollectionId: collectionFilter || undefined,
          status: statusFilter || undefined,
        },
      }),
  });

  const fromSkuQueryResult = useQuery<{ skus: DocumentSkuOption[] }>({
    queryKey: ["documents", "transition-sku-search", "from", fromSkuQuery],
    queryFn: () => apiFetch<{ skus: DocumentSkuOption[] }>("/api/document-skus", { query: { q: fromSkuQuery } }),
    enabled: createOpen,
  });

  const toSkuQueryResult = useQuery<{ skus: DocumentSkuOption[] }>({
    queryKey: ["documents", "transition-sku-search", "to", toSkuQuery],
    queryFn: () => apiFetch<{ skus: DocumentSkuOption[] }>("/api/document-skus", { query: { q: toSkuQuery } }),
    enabled: createOpen,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ transitionNotice: ProductTransitionNotice }>("/api/document-transition-notices", {
        method: "POST",
        body: {
          productCollectionId,
          noticeNumber: noticeNumber.trim(),
          title: title.trim(),
          transitionType,
          fromSku: fromSku || null,
          toSku: toSku || null,
          effectiveRule: effectiveRule.trim(),
          effectiveDate: effectiveDate || null,
          appliesTo: appliesTo.trim() || null,
          reason: reason.trim() || null,
          notes: notes.trim() || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setCreateOpen(false);
      setProductCollectionId("");
      setNoticeNumber("");
      setTitle("");
      setTransitionType("runout");
      setFromSku("");
      setToSku("");
      setFromSkuQuery("");
      setToSkuQuery("");
      setEffectiveRule("");
      setEffectiveDate("");
      setAppliesTo("Manufacturing, planning, purchasing, sales order fulfillment");
      setReason("");
      setNotes("");
    },
  });

  const actionMutation = useMutation({
    mutationFn: (input: { id: string; action: "issue" | "complete" | "cancel" }) =>
      apiFetch<{ ok: true }>(`/api/document-transition-notices/${input.id}/${input.action}`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const collections = collectionsQuery.data?.collections ?? [];
  const notices = noticesQuery.data?.transitionNotices ?? [];
  const fromSkuOptions = useMemo(
    () => [
      { value: "", label: "None" },
      ...(fromSkuQueryResult.data?.skus ?? []).map((sku) => ({
        value: sku.sku,
        label: `${sku.sku}${sku.product_name ? ` - ${sku.product_name}` : ""}`,
      })),
    ],
    [fromSkuQueryResult.data?.skus],
  );
  const toSkuOptions = useMemo(
    () => [
      { value: "", label: "None" },
      ...(toSkuQueryResult.data?.skus ?? []).map((sku) => ({
        value: sku.sku,
        label: `${sku.sku}${sku.product_name ? ` - ${sku.product_name}` : ""}`,
      })),
    ],
    [toSkuQueryResult.data?.skus],
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitleWithHelp hoverHint="Issue, complete, or cancel Product Transition Notices. Product context lives in the workbench.">
            Transition queue
          </PageTitleWithHelp>
        </div>
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
        <HoverHintTrigger
          trigger={
            <Button
              type="button"
              variant="primary"
              disabled={!hubWritesAllowed}
              onClick={() => setCreateOpen(true)}
            >
              Create transition notice
            </Button>
          }
        >
          Open the form to record a new Product Transition Notice for a collection (cutover, runout, dates, and so on).
        </HoverHintTrigger>
        </ReadOnlyHoverLayer>
      </header>

      {createOpen ? (
        <DocumentModal
          title="Create Product Transition Notice"
          description="Use this for collection-level cutovers such as FI-3C moving from 1609 to 1610."
          onClose={() => setCreateOpen(false)}
          showCloseButton={false}
        >
          <div className="space-y-4">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle} layout="block">
            <fieldset
              disabled={!hubWritesAllowed || createMutation.isPending}
              className="space-y-4 border-0 p-0"
            >
            <div className="grid gap-4 lg:grid-cols-3">
              <label className="block text-sm font-medium text-slate-700">
                Collection
                <select
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  value={productCollectionId}
                  onChange={(event) => setProductCollectionId(event.target.value)}
                >
                  <option value="">Choose collection</option>
                  {collections.map((collection) => (
                    <option key={collection.id} value={collection.id}>
                      {collection.code} - {collection.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Notice number
                <Input className="mt-1" value={noticeNumber} onChange={(event) => setNoticeNumber(event.target.value)} />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Transition type
                <select
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  value={transitionType}
                  onChange={(event) =>
                    setTransitionType(event.target.value as ProductTransitionNotice["transition_type"])
                  }
                >
                  {TRANSITION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-3">
                Title
                <Input className="mt-1" value={title} onChange={(event) => setTitle(event.target.value)} />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                From part number
                <SkuSearchableSelect
                  className="mt-1"
                  value={fromSku}
                  onValueChange={(option) => setFromSku(option?.value ?? "")}
                  options={fromSkuOptions}
                  searchValue={fromSkuQuery}
                  onSearchChange={setFromSkuQuery}
                  placeholder="None"
                  searchPlaceholder="Search SKU"
                  emptyMessage={fromSkuQuery.trim() ? "No SKUs found." : "Type to search SKUs."}
                  isLoading={fromSkuQueryResult.isFetching}
                  selectedLabelOverride={fromSku}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                To part number
                <SkuSearchableSelect
                  className="mt-1"
                  value={toSku}
                  onValueChange={(option) => setToSku(option?.value ?? "")}
                  options={toSkuOptions}
                  searchValue={toSkuQuery}
                  onSearchChange={setToSkuQuery}
                  placeholder="None"
                  searchPlaceholder="Search SKU"
                  emptyMessage={toSkuQuery.trim() ? "No SKUs found." : "Type to search SKUs."}
                  isLoading={toSkuQueryResult.isFetching}
                  selectedLabelOverride={toSku}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700">
                Effective date
                <Input
                  className="mt-1"
                  type="date"
                  value={effectiveDate}
                  onChange={(event) => setEffectiveDate(event.target.value)}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-2">
                Applies to
                <Input className="mt-1" value={appliesTo} onChange={(event) => setAppliesTo(event.target.value)} />
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-3">
                Effective rule
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={3}
                  value={effectiveRule}
                  onChange={(event) => setEffectiveRule(event.target.value)}
                  placeholder="Example: Continue building 1609 until current orders are fulfilled, then build 1610 for FI-3C orders."
                />
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-3">
                Reason
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <label className="block text-sm font-medium text-slate-700 lg:col-span-3">
                Notes
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm"
                  rows={2}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
            </div>
            </fieldset>
            </ReadOnlyHoverLayer>
            {createMutation.isError ? (
              <p className="text-sm text-red-600">{(createMutation.error as Error).message}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <HoverHintTrigger
                trigger={
                  <Button type="button" variant="ghost" disabled={createMutation.isPending} onClick={() => setCreateOpen(false)}>
                    Cancel
                  </Button>
                }
              >
                Close the form without creating a notice.
              </HoverHintTrigger>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    variant="primary"
                    disabled={
                      createMutation.isPending ||
                      productCollectionId === "" ||
                      noticeNumber.trim() === "" ||
                      title.trim() === "" ||
                      effectiveRule.trim() === "" ||
                      (fromSku === "" && toSku === "") ||
                      !hubWritesAllowed
                    }
                    onClick={() => createMutation.mutate()}
                  >
                    Create notice
                  </Button>
                }
              >
                Save a draft Product Transition Notice with the collection, part numbers, and effective rule you entered.
              </HoverHintTrigger>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </DocumentModal>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Transition notice queue</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Collection
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                value={collectionFilter}
                onChange={(event) => setCollectionFilter(event.target.value)}
              >
                <option value="">All collections</option>
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.code} - {collection.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
              Status
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="issued">Issued</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          {noticesQuery.isError ? (
            <p className="text-sm text-red-600">{noticesQuery.error.message}</p>
          ) : noticesQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : notices.length === 0 ? (
            <p className="text-sm text-slate-500">No transition notices match these filters.</p>
          ) : (
            <div className="space-y-3">
              {notices.map((notice) => (
                <section key={notice.id} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold text-slate-900">
                          {notice.notice_number} - {notice.title}
                        </h2>
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(notice.status)}`}>
                          {notice.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        {notice.product_collection_code} - {notice.product_collection_name}
                      </p>
                      <p className="mt-1 text-sm text-slate-700">
                        {skuLabel(notice.from_sku, notice.from_sku_product_name)} →{" "}
                        {skuLabel(notice.to_sku, notice.to_sku_product_name)}
                      </p>
                      <p className="mt-2 text-sm text-slate-700">{notice.effective_rule}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {notice.transition_type.replaceAll("_", " ")}
                        {notice.effective_date ? ` · effective ${notice.effective_date}` : ""} · updated{" "}
                        {formatTimestamp(notice.updated_at)}
                      </p>
                      {notice.reason ? <p className="mt-2 text-sm text-slate-600">{notice.reason}</p> : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <HoverHintTrigger
                        trigger={
                          <Link to="/documents">
                            <Button type="button" size="sm" variant="ghost">
                              Open workbench
                            </Button>
                          </Link>
                        }
                      >
                        Go to Document Control Workbench to review documents and context for this notice.
                      </HoverHintTrigger>
                      {notice.status === "draft" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: notice.id, action: "issue" })}
                            >
                              Issue
                            </Button>
                          }
                        >
                          Publish this draft notice so teams can act on it (status becomes issued).
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {notice.status === "issued" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: notice.id, action: "complete" })}
                            >
                              Complete
                            </Button>
                          }
                        >
                          Mark the cutover or transition as finished when work matches the notice.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {notice.status === "draft" || notice.status === "issued" ? (
                        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={documentsReadOnlyTitle}>
                        <HoverHintTrigger
                          trigger={
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              disabled={actionMutation.isPending || !hubWritesAllowed}
                              onClick={() => actionMutation.mutate({ id: notice.id, action: "cancel" })}
                            >
                              Cancel
                            </Button>
                          }
                        >
                          Void this notice; use when the transition will not happen or was recorded in error.
                        </HoverHintTrigger>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}
          {actionMutation.isError ? (
            <p className="mt-3 text-sm text-red-600">{(actionMutation.error as Error).message}</p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
